import { SERVICES, PACKAGES, SERVICE_IMAGES } from "../lib/constants";
import { Check, ArrowUpRight, FileText } from "lucide-react";
import { useState } from "react";
import { QuoteDialog } from "./QuoteDialog";

const QUOTE_SERVICE_IDS = new Set(["ppf", "colored-ppf", "wrapping"]);

export const Services = ({ onBook }) => {
  const [quoteFor, setQuoteFor] = useState(null);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#0A0A0A] py-24 md:py-32 noise-overlay"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="red-bar" />
              <span className="font-mono-accent text-xs text-zinc-400">01 — WHAT WE DO</span>
            </div>
            <h2 className="font-display font-bold uppercase text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tighter">
              Services <br />
              <span className="chrome-text">tailored</span> to your <br />
              ride.
            </h2>
          </div>
          <p className="md:max-w-sm text-zinc-400 text-base leading-relaxed">
            From a quick maintenance wash to multi-day ceramic transformations — every job is
            finished by hand with professional-grade products.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {/* Ceramic — large feature */}
          <div
            data-testid="service-card-ceramic-coating"
            className="md:col-span-7 relative bg-[#121212] border border-[#27272A] hover:border-[#52525B] transition-colors min-h-[420px] overflow-hidden group"
          >
            <img
              src={SERVICE_IMAGES.ceramic}
              alt="Ceramic coating water beading"
              className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
            <div className="relative h-full p-8 md:p-12 flex flex-col justify-end">
              <div className="font-mono-accent text-xs text-[#DC2626] mb-3">FEATURED</div>
              <h3 className="font-display font-bold uppercase text-white text-4xl md:text-5xl tracking-tighter mb-3">
                Ceramic Coating
              </h3>
              <p className="text-zinc-300 max-w-md mb-4">
                Nano-ceramic protection that bonds with your paint — hydrophobic, chemical-resistant,
                glass-like shine for up to 5 years.
              </p>
              <div className="flex items-center gap-6 text-xs font-mono-accent text-zinc-400">
                <span>FROM $1,200</span>
                <span className="w-px h-3 bg-zinc-700" />
                <span>1–2 DAYS</span>
              </div>
            </div>
          </div>

          {/* Interior */}
          <div
            data-testid="service-card-interior-detail"
            className="md:col-span-5 relative bg-[#121212] border border-[#27272A] hover:border-[#52525B] transition-colors min-h-[420px] overflow-hidden group"
          >
            <img
              src={SERVICE_IMAGES.interior}
              alt="Premium leather interior"
              className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
            <div className="relative h-full p-8 md:p-12 flex flex-col justify-end">
              <h3 className="font-display font-bold uppercase text-white text-4xl md:text-5xl tracking-tighter mb-3">
                Interior <br />Detailing
              </h3>
              <p className="text-zinc-300 mb-4">
                Steam extraction, leather feed & seal, dashboard & trim conditioning.
              </p>
              <div className="flex items-center gap-6 text-xs font-mono-accent text-zinc-400">
                <span>FROM $200</span>
                <span className="w-px h-3 bg-zinc-700" />
                <span>3 HRS</span>
              </div>
            </div>
          </div>

          {/* Remaining 4 services as compact cards */}
          {SERVICES.filter(
            (s) => !["ceramic-coating", "interior-detail"].includes(s.id)
          ).map((s, idx) => {
            const isQuote = QUOTE_SERVICE_IDS.has(s.id);
            return (
              <div
                key={s.id}
                data-testid={`service-card-${s.id}`}
                className="relative bg-[#121212] border border-[#27272A] hover:border-[#DC2626] hover:bg-[#121212]/80 transition-all duration-300 p-8 md:p-10 group md:col-span-6"
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="font-mono-accent text-xs text-zinc-500">
                    0{idx + 3}
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-zinc-600 group-hover:text-[#DC2626] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                </div>
                <h3 className="font-display font-bold uppercase text-white text-3xl md:text-4xl tracking-tighter mb-3">
                  {s.title}
                </h3>
                <p className="text-zinc-400 mb-6 leading-relaxed">{s.description}</p>
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-4 text-xs font-mono-accent text-zinc-500">
                    <span className={isQuote ? "text-[#DC2626]" : "text-white"}>{s.price}</span>
                    <span className="w-px h-3 bg-zinc-700" />
                    <span>{s.duration}</span>
                  </div>
                  {isQuote && (
                    <button
                      data-testid={`quote-btn-${s.id}`}
                      onClick={() => setQuoteFor(s.title)}
                      className="inline-flex items-center gap-2 bg-[#DC2626] hover:bg-[#EF4444] text-white font-display font-bold uppercase tracking-wider text-xs px-4 py-2.5 transition-colors"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      Get a Quote
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Packages */}
        <div className="mt-32">
          <div className="flex items-center gap-3 mb-6">
            <span className="red-bar" />
            <span className="font-mono-accent text-xs text-zinc-400">02 — DETAIL PACKAGES</span>
          </div>
          <h2 className="font-display font-bold uppercase text-white text-5xl md:text-7xl leading-[0.9] tracking-tighter mb-12">
            Pick your <span className="chrome-text">tier</span>.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {PACKAGES.map((p) => (
              <div
                key={p.id}
                data-testid={`package-${p.id}`}
                className={`relative p-8 md:p-10 border transition-colors ${
                  p.featured
                    ? "bg-[#DC2626] border-[#DC2626] text-white"
                    : "bg-[#121212] border-[#27272A] hover:border-[#52525B]"
                }`}
              >
                {p.featured && (
                  <div className="absolute -top-3 left-8 bg-black text-[#DC2626] font-mono-accent text-[10px] px-3 py-1">
                    MOST POPULAR
                  </div>
                )}
                <div className={`font-mono-accent text-xs mb-4 ${p.featured ? "text-white/70" : "text-zinc-500"}`}>
                  {p.tagline}
                </div>
                <h3 className="font-display font-bold uppercase text-5xl tracking-tighter mb-2">{p.name}</h3>
                <div className="font-display text-3xl mb-8">{p.price}</div>
                <ul className="space-y-3 mb-8">
                  {p.items.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-sm">
                      <Check className={`h-4 w-4 mt-0.5 flex-shrink-0 ${p.featured ? "text-white" : "text-[#DC2626]"}`} />
                      <span className={p.featured ? "text-white" : "text-zinc-300"}>{it}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`package-${p.id}-btn`}
                  onClick={onBook}
                  className={`w-full font-display font-bold uppercase tracking-wider text-sm py-4 transition-colors ${
                    p.featured
                      ? "bg-black text-white hover:bg-zinc-900"
                      : "bg-[#DC2626] text-white hover:bg-[#EF4444]"
                  }`}
                >
                  Book {p.name}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <QuoteDialog service={quoteFor} open={!!quoteFor} onClose={() => setQuoteFor(null)} />
    </section>
  );
};
